import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    ReportingTagBreadcrumbs,
    ReportingTagCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as ReportingTagActions from "../../../actions/reportingTags";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { QALookupsLayout } from "../../index";

import "./ReportingTagPage.scss";

interface ReportingTagPageProps {
    form: Dtos.ReportingTag & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reportingTag: Dtos.ReportingTag;
    lookups: Dtos.Lookup[];
    loadingReportingTag: boolean;
    loadReportingTagFailure: boolean;
    loadReportingTagErrorCode: string;
    savingReportingTag: boolean;
    saveReportingTagSuccess: boolean;
    saveReportingTagFailure: boolean;
    creatingReportingTag: boolean;
    createReportingTagSuccess: boolean;
    validatingReportingTag: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearReportingTag: ReportingTagActions.IReportingTagClearActionCreator;
    loadReportingTag: ReportingTagActions.IReportingTagLoadByIdActionCreator;
    saveReportingTag: ReportingTagActions.IReportingTagSaveActionCreator;
    createReportingTag: ReportingTagActions.IReportingTagCreateActionCreator;
    validateReportingTag: ReportingTagActions.IReportingTagValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.reportingTag";

const ReportingTagPage: FC<ReportingTagPageProps> = ({
    form,
    formState,
    formProperties,
    reportingTag,
    lookups,
    loadingReportingTag,
    loadReportingTagFailure,
    loadReportingTagErrorCode,
    savingReportingTag,
    saveReportingTagSuccess,
    saveReportingTagFailure,
    creatingReportingTag,
    createReportingTagSuccess,
    validatingReportingTag,
    validationFailures,
    permissions,
    clearReportingTag,
    loadReportingTag,
    saveReportingTag,
    createReportingTag,
    validateReportingTag,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { reportingTagId } = useParams<{ reportingTagId: string }>();
    const reportingTagIdNumber = parseInt(reportingTagId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ReportingTag = {} as Dtos.ReportingTag;

        loadForm(reduxFormName, Object.assign({ ...reportingTag }, extraData));
    };


    // Load reporting tag
    useEffect(() => {
        if (reportingTagIdNumber && !reportingTag && !loadingReportingTag) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(ReportingTagBreadcrumbs(reportingTagIdNumber));
            loadReportingTag(reportingTagIdNumber);
        } else if (
            !reportingTagIdNumber &&
            !creatingReportingTag &&
            !createReportingTagSuccess
        ) {
            createReportingTag();
            setBreadcrumbs(ReportingTagCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (reportingTag && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        reportingTagIdNumber,
        reportingTag,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        ReportingTagBreadcrumbs,
        loadReportingTag,
        createReportingTag,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingReportingTag,
        creatingReportingTag,
        createReportingTagSuccess,
        ReportingTagCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadReportingTagFailure) {
            navigate(`/error/${loadReportingTagErrorCode}`);
        }
    }, [loadReportingTagFailure, loadReportingTagErrorCode, navigate]);

    // Update breadcrumbs
    useEffect(() => {
        if (reportingTagIdNumber && reportingTag?.value) {
            setBreadcrumbs(
                ReportingTagBreadcrumbs(
                    reportingTagIdNumber,
                    reportingTag?.value
                )
            );
        }
    }, [
        reportingTagIdNumber,
        reportingTag,
        setBreadcrumbs,
        ReportingTagBreadcrumbs,
    ]);

    // Reload reporting tag after saving
    useEffect(() => {
        if (!savingReportingTag && saveReportingTagSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Reporting tag saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/qa/reportingtags";
                navigate(url);
            } else if (!reportingTagIdNumber) {
                let url: string = `/settings/qa/reportingtags/${reportingTag?.id}`;
                navigate(url);
                clearReportingTag();
            }
        }
    }, [
        savingReportingTag,
        saveReportingTagSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingReportingTag && saveReportingTagFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingReportingTag, saveReportingTagFailure]);

    // Cleanup effect to clear the reporting tags on component unmount
    useEffect(() => {
        return () => {
            clearReportingTag();
            resetForm(reduxFormName);
        };
    }, [clearReportingTag, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/qa/reportingtags";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.ReportingTag) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ReportingTag = {} as Dtos.ReportingTag;

            await saveReportingTag(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ReportingTag = {} as Dtos.ReportingTag;

            if (form && hasBeenSubmitted && !validatingReportingTag) {
                await validateReportingTag(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ReportingTagsUpdate,
                Dtos.Permission.ReportingTagsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingReportingTag ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "reportingTag",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "reportingTag",
                                        {
                                            reportingTagCategoryId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "ReportingTag",
                                                                "ReportingTagCategoryId",
                                                                lookups
                                                            ),
                                                            form.reportingTagCategoryId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                                changeAction: (
                                                    model,
                                                    value,
                                                    _form,
                                                    _parentModel
                                                ) => {
                                                    changeForm(model, value);
                                                    changeForm(
                                                        reduxFormName +
                                                            ".reportingTagCategory",
                                                        null
                                                    );
                                                },
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "reportingTag",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "reportingTag",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "reportingTag",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingReportingTag,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <QALookupsLayout
            loading={loadingReportingTag}
            permission={Dtos.Permission.ReportingTagsView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<ReportingTagPageProps> => {
    let reportingTag: Dtos.ReportingTag | undefined = !(
        state.reportingTags.formData instanceof Array
    )
        ? state.reportingTags.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        reportingTag: reportingTag!,
        loadingReportingTag:
            state.reportingTags.loadState &&
            state.reportingTags.loadState.status == RequestState.Pending,
        loadReportingTagFailure:
            state.reportingTags.loadState &&
            state.reportingTags.loadState.status == RequestState.Failure,
        loadReportingTagErrorCode: state.reportingTags.loadState.errorCode,
        savingReportingTag:
            state.reportingTags.saveState &&
            state.reportingTags.saveState.status == RequestState.Pending,
        saveReportingTagSuccess:
            state.reportingTags.saveState &&
            state.reportingTags.saveState.status == RequestState.Success,
        saveReportingTagFailure:
            state.reportingTags.saveState &&
            state.reportingTags.saveState.status == RequestState.Failure,
        creatingReportingTag:
            state.reportingTags.createState &&
            state.reportingTags.createState.status == RequestState.Pending,
        createReportingTagSuccess:
            state.reportingTags.createState &&
            state.reportingTags.createState.status == RequestState.Success,
        validatingReportingTag:
            state.reportingTags.validateState &&
            state.reportingTags.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.reportingTags.lookups,
        form: state.reduxForms.reportingTag,
        formState: state.reportingTags.formState,
        formProperties: state.reportingTags.formProperties,
        validationFailures: reportingTag
            ? state.reportingTags.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<ReportingTagPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadReportingTag: bindActionCreators(
            ReportingTagActions.LoadReportingTagById,
            dispatch
        ),
        clearReportingTag: bindActionCreators(
            ReportingTagActions.Clear,
            dispatch
        ),
        saveReportingTag: bindActionCreators(
            ReportingTagActions.SaveReportingTag,
            dispatch
        ),
        createReportingTag: bindActionCreators(
            ReportingTagActions.CreateReportingTag,
            dispatch
        ),
        validateReportingTag: bindActionCreators(
            ReportingTagActions.ValidateReportingTag,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportingTagPage);
